import React, { useEffect } from 'react';
import { standingOf } from '../../lib/subcontractors';

/* Subcontractor Mgmt's small pieces: its dialogs, fields, buttons and the standing pill, in the Team
   page's look (UserManagementPage). */

export const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
export const label = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' };

export const StandingPill = ({ standing }) => {
  const s = standingOf(standing);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
      color: s.tone, background: s.soft, border: `1px solid color-mix(in oklch, ${s.tone} 18%, transparent)`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: s.tone, flexShrink: 0 }} />
      {s.label}
    </span>
  );
};

/** A status in words, coloured when it needs attention (an expiry, a missing document). */
export const StatusText = ({ status, style }) => (
  <span style={{ color: status.tone || 'var(--text-2)', fontWeight: status.tone ? 600 : 400, whiteSpace: 'nowrap', ...style }}>{status.text}</span>
);

const btnBase = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8,
  fontSize: 14, whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)',
};
export const PrimaryButton = ({ children, busy, disabled, style, ...rest }) => (
  <button type="button" disabled={busy || disabled} {...rest} style={{
    ...btnBase, padding: '0 15px', fontWeight: 600, color: '#fff', background: 'var(--accent)',
    border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
    boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)',
    cursor: busy || disabled ? 'default' : 'pointer', opacity: busy ? 0.85 : disabled ? 0.55 : 1, ...style,
  }}>{children}</button>
);
export const SecondaryButton = ({ children, busy, disabled, danger, style, ...rest }) => (
  <button type="button" disabled={busy || disabled} {...rest} style={{
    ...btnBase, fontWeight: 500, color: danger ? 'var(--danger)' : 'var(--text-2)', background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)',
    cursor: busy || disabled ? 'default' : 'pointer', opacity: busy || disabled ? 0.6 : 1, ...style,
  }}>{children}</button>
);

export const ErrorBanner = ({ children }) => (
  <div role="alert" style={{
    padding: '9px 12px', borderRadius: 9, fontSize: 13.5, lineHeight: 1.45, color: 'var(--danger)',
    background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 22%, transparent)',
  }}>{children}</div>
);

// fill: the dialog takes 90% of the window's height and its body lays out as a column, for a child that fills it.
export const ModalShell = ({ title, subtitle, onClose, busy, children, footer, width = 560, fill = false }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} style={{
      // One column the overlay's width, so a dialog's maxWidth 100% holds on a phone (an auto column grows to its width).
      position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', placeItems: 'center',
      background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', padding: 24,
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} style={{
        width, maxWidth: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 14,
        boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)', overflow: 'hidden',
        fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em',
        display: 'flex', flexDirection: 'column', maxHeight: '90%', minHeight: 0, ...(fill ? { height: '90%' } : {}),
      }}>
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>{subtitle}</p>}
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" style={{
            width: 26, height: 26, borderRadius: 6, color: 'var(--text-3)', flex: '0 0 auto', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', background: 'transparent', border: '1px solid transparent', cursor: busy ? 'default' : 'pointer',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px', ...(fill ? { display: 'flex', flexDirection: 'column' } : {}) }}>{children}</div>
        {footer && (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>{footer}</footer>
        )}
      </div>
    </div>
  );
};

export const Label = ({ children, required, hint, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '0.01em', marginBottom: 6 }}>
    <span>{children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}</span>
    {hint && <span style={{ fontWeight: 500, color: 'var(--text-4)', letterSpacing: 0 }}>{hint}</span>}
  </label>
);

export const fieldStyle = {
  width: '100%', height: 36, padding: '0 12px', borderRadius: 8, fontFamily: 'var(--font-sans)', fontSize: 14.5,
  color: 'var(--text-1)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxSizing: 'border-box',
};

/** A labelled field. `as`: 'input' (default), 'textarea' or 'select' (with `options` [{ value, label }]). */
export const Field = ({ id, label: text, required, hint, as = 'input', options, value, onChange, style, ...rest }) => {
  const common = { id, value: value ?? '', onChange: (e) => onChange(e.target.value), className: 'sub-field', ...rest };
  return (
    <div style={{ minWidth: 0 }}>
      {text && <Label htmlFor={id} required={required} hint={hint}>{text}</Label>}
      {as === 'textarea' ? (
        <textarea {...common} style={{ ...fieldStyle, height: 'auto', minHeight: 84, padding: '9px 12px', lineHeight: 1.45, resize: 'vertical', ...style }} />
      ) : as === 'select' ? (
        <select {...common} style={{ ...fieldStyle, ...style }}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input {...common} style={{ ...fieldStyle, ...style }} />
      )}
    </div>
  );
};

/* Focus rings for the fields above (one stylesheet, rendered once by each page). */
export const SubStyles = () => (
  <style>{`
    .sub-field:focus { outline: none; border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-ring); }
    .sub-row:hover { background: color-mix(in oklch, var(--bg-hover) 55%, transparent); }
    .sub-row:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
  `}</style>
);
